"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { addAakRule, editAakRule } from "@/app/lib/actions/aak-rules";
import type { AakRule } from "@/prisma/generated/client";

type FormValues = {
  saleNature: string;
  label: string;
  accountingFlag: string;
  deliveryNature: string;
  networkFlag: string;
  warrantyStart: string;
  versionNote: string;
};

const DEFAULT_FORM: FormValues = {
  saleNature: "",
  label: "",
  accountingFlag: "I",
  deliveryNature: "L",
  networkFlag: "O",
  warrantyStart: "A",
  versionNote: "",
};

interface AakRuleFormProps {
  rule: AakRule | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function AakRuleForm({ rule, open, onClose, onSuccess }: AakRuleFormProps) {
  const isNew = !rule;

  const [form, setForm] = useState<FormValues>(() =>
    rule
      ? {
          saleNature: rule.saleNature,
          label: rule.label,
          accountingFlag: rule.accountingFlag,
          deliveryNature: rule.deliveryNature,
          networkFlag: rule.networkFlag ?? "O",
          warrantyStart: rule.warrantyStart ?? "NONE",
          versionNote: "",
        }
      : DEFAULT_FORM,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const set = (key: keyof FormValues) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const payload = {
      ...(isNew ? {} : { id: rule.id }),
      saleNature: form.saleNature.toUpperCase(),
      label: form.label,
      accountingFlag: form.accountingFlag as "F" | "I" | "T",
      deliveryNature: form.deliveryNature as "L" | "D" | "R" | "Z",
      networkFlag: form.networkFlag as "N" | "O" | "T",
      warrantyStart: form.warrantyStart === "A" ? ("A" as const) : null,
      isActive: true,
      ...(isNew ? {} : { versionNote: form.versionNote || undefined }),
    };

    const result = isNew
      ? await addAakRule(payload)
      : await editAakRule(payload);

    setLoading(false);

    if (!result.success) {
      setError(
        typeof result.error === "string"
          ? result.error
          : "Erreur de validation",
      );
      return;
    }

    onSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? "Nouvelle nature de vente"
              : `Modifier — ${rule.saleNature} · ${rule.label}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Code nature</Label>
              <Input
                maxLength={1}
                value={form.saleNature}
                onChange={(e) =>
                  set("saleNature")(e.target.value.toUpperCase())
                }
                disabled={!isNew}
                placeholder="A"
                className="font-mono tracking-widest uppercase"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Comptabilisation AAK</Label>
              <Select
                value={form.accountingFlag}
                onValueChange={set("accountingFlag")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="F">F</SelectItem>
                  <SelectItem value="I">I</SelectItem>
                  <SelectItem value="T">T</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Libellé</Label>
            <Input
              value={form.label}
              onChange={(e) => set("label")(e.target.value)}
              placeholder="Ex : RAC DIRECT"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Nature livraison</Label>
              <Select
                value={form.deliveryNature}
                onValueChange={set("deliveryNature")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">L</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                  <SelectItem value="R">R</SelectItem>
                  <SelectItem value="Z">Z</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Flag réseau</Label>
              <Select
                value={form.networkFlag}
                onValueChange={set("networkFlag")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="N">N</SelectItem>
                  <SelectItem value="O">O</SelectItem>
                  <SelectItem value="T">T</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Départ garantie</Label>
              <Select
                value={form.warrantyStart}
                onValueChange={set("warrantyStart")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="NONE">—</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isNew && (
            <div className="space-y-2">
              <Label>
                Note de modification{" "}
                <span className="text-muted-foreground font-normal">
                  (optionnel)
                </span>
              </Label>
              <Input
                value={form.versionNote}
                onChange={(e) => set("versionNote")(e.target.value)}
                placeholder="Motif de la modification…"
              />
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Enregistrement…" : isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { AakRuleForm };
export type { AakRuleFormProps };
